import { ClipboardEvent, Fragment, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, IconButton, TextField } from "@mui/material";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import RemoveOutlined from "@mui/icons-material/RemoveOutlined";
import { formatFileSize } from "../../utils/formatFileSize";
import { formatCurrency } from "../../utils/formatCurrency";
import { InquiryTemplateItem, TEMPLATE_LANGUAGES, TemplateGroup, TemplateLanguage } from "./types";
import { BUTTON, INK, OFFER_BG, OFFER_BORDER, OUTLINED, pill } from "./styles";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
const ATTACHMENT_NAME = /\.(png|jpe?g|pdf)$/i;
const PCT_STEP = 10;
const PCT_MIN = 10;
const PCT_MAX = 90;
const GROUPS: { group: TemplateGroup; label: string }[] = [{ group: 'explain', label: 'Explain' }, { group: 'offer', label: 'Offer' }];
const GROUP_LABEL = { fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#98a2b3' } as const;

export type ComposerOffer = { pct: number; amount: number; total: number; currency: string; allowed: boolean };

type Props = {
  templates: InquiryTemplateItem[];
  templateKey: string | null;
  onTemplate: (template: InquiryTemplateItem) => void;
  pct: number;
  onPct: (pct: number) => void;
  language: TemplateLanguage | null;
  languageDetected: boolean;
  onLanguage: (language: TemplateLanguage) => void;
  draft: string;
  onDraft: (text: string) => void;
  attachment: File | null;
  onAttachment: (file: File | null) => void;
  onSend: () => void;
  onSendAndNext: () => void;
  offer: ComposerOffer | null;
  onSendOffer: () => void;
  onSendOfferAndNext: () => void;
  sending: boolean;
  disabled: boolean;
  disabledReason: string;
  placeholder?: string;
  maxLength: number;
};

export default function InquiryComposer({
  templates, templateKey, onTemplate, pct, onPct, language, languageDetected, onLanguage, draft, onDraft, attachment, onAttachment, onSend, onSendAndNext, offer, onSendOffer, onSendOfferAndNext, sending, disabled, disabledReason, placeholder, maxLength,
}: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const selectPct = useRef<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  useEffect(() => {
    const token = selectPct.current;
    const start = token === null ? -1 : draft.indexOf(token);
    if (start < 0) return;
    selectPct.current = null;
    textarea.current?.focus();
    textarea.current?.setSelectionRange(start, start + token!.length);
  }, [draft]);
  const step = (delta: number) => {
    const next = Math.min(PCT_MAX, Math.max(PCT_MIN, pct + delta));
    if (next === pct) return;
    onPct(next);
    if (!draft.includes(`${pct}%`)) return;
    selectPct.current = `${next}%`;
    onDraft(draft.replace(`${pct}%`, `${next}%`));
  };
  const pick = (file: File) => {
    if (!ATTACHMENT_TYPES.includes(file.type) || !ATTACHMENT_NAME.test(file.name)) setFileError("Only PNG, JPEG or PDF files can be attached.");
    else if (file.size > MAX_ATTACHMENT_BYTES) setFileError("Files over 10 MB cannot be attached.");
    else { setFileError(null); onAttachment(file); }
  };
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const file = Array.from(event.clipboardData.files).find((candidate) => ATTACHMENT_TYPES.includes(candidate.type));
    if (!file) return;
    event.preventDefault();
    pick(file);
  };
  const length = draft.length;
  const over = length > maxLength;
  const blocked = disabled || over || !draft.trim();
  const locked = disabled || sending;
  const offerWithFile = offer?.allowed === true && attachment !== null;
  if (disabled) {
    return (
      <Box sx={{ bgcolor: '#fff', border: `1px solid ${INK.border}`, borderRadius: '10px', p: '12px', fontSize: 12, color: INK.muted }}>{disabledReason}</Box>
    );
  }
  return (
    <Box data-mode={offer?.allowed ? 'offer' : 'reply'} sx={{ bgcolor: '#fff', border: `1px solid ${offer?.allowed ? OFFER_BORDER : INK.border}`, borderRadius: '10px', p: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', rowGap: '6px', columnGap: '8px', alignItems: 'center' }}>
        {GROUPS.filter(({ group }) => templates.some((t) => t.group === group)).map(({ group, label }) => (
          <Fragment key={group}>
            <Box component="span" sx={GROUP_LABEL}>{label}</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
              {templates.filter((t) => t.group === group).map((template) => (
                <Chip
                  key={template.key}
                  size="small"
                  label={template.label}
                  variant="outlined"
                  disabled={locked}
                  clickable={!locked}
                  onClick={() => onTemplate(template)}
                  onDelete={template.key === templateKey ? () => onTemplate(template) : undefined}
                  sx={pill(template.key === templateKey)}
                />
              ))}
            </Box>
          </Fragment>
        ))}
        {offer && (
          <>
            <Box component="span" sx={GROUP_LABEL}>Refund</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: 12 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 28, border: `1px solid ${INK.control}`, borderRadius: '8px', overflow: 'hidden' }}>
                <IconButton size="small" aria-label="Lower percentage" disabled={locked || pct <= PCT_MIN} onClick={() => step(-PCT_STEP)} sx={{ borderRadius: 0, width: 26, height: 26 }}>
                  <RemoveOutlined sx={{ fontSize: 14 }} />
                </IconButton>
                <Box component="span" sx={{ px: '8px', fontWeight: 600, color: INK.text, borderLeft: `1px solid ${INK.border}`, borderRight: `1px solid ${INK.border}`, lineHeight: '26px' }}>{pct}%</Box>
                <IconButton size="small" aria-label="Raise percentage" disabled={locked || pct >= PCT_MAX} onClick={() => step(PCT_STEP)} sx={{ borderRadius: 0, width: 26, height: 26 }}>
                  <AddOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <Box component="span" sx={{ color: INK.muted }}>·</Box>
              <Box component="span" sx={{ color: INK.body }}>{`${formatCurrency(offer.amount, offer.currency)} of ${formatCurrency(offer.total, offer.currency)}`}</Box>
            </Box>
          </>
        )}
        <Box component="span" sx={GROUP_LABEL}>Language</Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <Box role="group" aria-label="Reply language" sx={{ display: 'inline-flex', alignItems: 'center', height: 28, border: `1px solid ${INK.control}`, borderRadius: '8px', overflow: 'hidden' }}>
            {TEMPLATE_LANGUAGES.map((option, index) => (
              <Box
                key={option}
                component="button"
                type="button"
                aria-pressed={option === language}
                disabled={locked}
                onClick={() => onLanguage(option)}
                sx={{
                  height: 26, px: '8px', border: 0, borderLeft: index === 0 ? 0 : `1px solid ${INK.border}`, fontFamily: 'inherit', fontSize: 11, fontWeight: 600, cursor: locked ? 'default' : 'pointer',
                  bgcolor: option === language ? INK.tint : '#fff', color: option === language ? INK.primary : INK.muted,
                }}
              >
                {option.toUpperCase()}
              </Box>
            ))}
          </Box>
          {languageDetected && language && <Box component="span" sx={{ fontSize: 12, color: INK.muted }}>{`${language.toUpperCase()} · detected from buyer`}</Box>}
        </Box>
      </Box>
      <TextField
        multiline
        minRows={5}
        maxRows={8}
        value={draft}
        onChange={(e) => onDraft(e.target.value)}
        onPaste={onPaste}
        disabled={sending}
        placeholder={placeholder}
        inputRef={textarea}
        slotProps={{ htmlInput: { 'aria-label': 'Reply' } }}
        sx={{ '& .MuiInputBase-root': { fontSize: 13, lineHeight: 1.5, p: '10px', borderRadius: '8px' } }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', rowGap: '8px' }}>
        {offer?.allowed ? (
          <>
            <Button variant="contained" size="small" sx={BUTTON} disabled={blocked || offerWithFile} onClick={onSendOffer}>
              {`Send offer · ${formatCurrency(offer.amount, offer.currency)}`}
            </Button>
            <Button variant="outlined" size="small" sx={OUTLINED} disabled={blocked || offerWithFile} onClick={onSendOfferAndNext}>Send offer and next</Button>
            <Button variant="text" size="small" sx={{ ...BUTTON, color: INK.body }} disabled={blocked} onClick={onSend}>Send as message instead</Button>
          </>
        ) : offer ? (
          <>
            <Button variant="contained" size="small" sx={BUTTON} disabled={blocked} onClick={onSend}>Send as message</Button>
            <Button variant="outlined" size="small" sx={OUTLINED} disabled={blocked} onClick={onSendAndNext}>Send and next</Button>
          </>
        ) : (
          <>
            <Button variant="contained" size="small" sx={BUTTON} disabled={blocked} onClick={onSend}>Send reply</Button>
            <Button variant="outlined" size="small" sx={OUTLINED} disabled={blocked} onClick={onSendAndNext}>Send and next</Button>
          </>
        )}
        {attachment && (
          <Chip
            size="small"
            label={`${attachment.name} · ${formatFileSize(attachment.size)}`}
            onDelete={() => onAttachment(null)}
            sx={{ maxWidth: 220, fontSize: 12 }}
          />
        )}
        {fileError && <Box component="span" sx={{ fontSize: 12, color: INK.danger }}>{fileError}</Box>}
        <IconButton size="small" aria-label="Attach file" disabled={locked} onClick={() => fileInput.current?.click()} sx={{ ml: 'auto' }}>
          <AttachFileOutlined fontSize="small" />
        </IconButton>
        <input
          ref={fileInput}
          type="file"
          hidden
          accept={ATTACHMENT_TYPES.join(',')}
          aria-label="Attachment file"
          onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) pick(file); }}
        />
        <Box component="span" sx={{ fontSize: 12, color: over ? INK.danger : INK.muted, whiteSpace: 'nowrap' }}>
          {length.toLocaleString('en-US')} / {maxLength.toLocaleString('en-US')}
        </Box>
      </Box>
      {offer && (
        <Box sx={offer.allowed
          ? { fontSize: 11, color: '#b54708', bgcolor: OFFER_BG, border: `1px solid ${OFFER_BORDER}`, borderRadius: '8px', px: '10px', py: '6px' }
          : { fontSize: 11, color: INK.muted }}
        >
          {offerWithFile
            ? "Offers can't carry a file. Send it as a message, or remove the file."
            : offer.allowed
            ? `Goes to PayPal as a refund offer of ${formatCurrency(offer.amount, offer.currency)}. The buyer gets the text and an Accept button.`
            : 'PayPal takes an offer only while it waits for you. This goes as a message; the buyer has to answer first.'}
        </Box>
      )}
    </Box>
  );
}
